import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Col,
  Form,
  Input,
  InputNumber,
  Modal,
  Pagination,
  Popconfirm,
  Radio,
  Row,
  Space,
  Switch,
  Table,
  Tooltip,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { BiEdit } from "react-icons/bi";
import { IoTrashOutline } from "react-icons/io5";
import PageHeader from "../../component/pageHeaders";
import { API } from "../../config/API";
import { DELETE, GET, PATCH, POST, PUT } from "../../utils/apirequest";
import { RegionDto } from "../../config/regions";
import { loadEpaperConfig } from "../../config/epaperConfig";
import { apiErrorMessage } from "./utils";
import "./styles.css";

type FormValues = {
  name: string;
  code: string;
  is_active: boolean;
  sort_order: number;
};

function EpaperRegions() {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [rows, setRows] = useState<RegionDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState<RegionDto | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [togglingId, setTogglingId] = useState<number | null>(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const load = async () => {
    setLoading(true);
    try {
      const res: any = await GET(API.EPAPER_SETTINGS_REGIONS, null, null);
      setRows(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not load regions."));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Keep the shared lists used by the e-paper forms in step.
  const afterChange = async () => {
    await Promise.all([load(), loadEpaperConfig(true)]);
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) =>
      [r.name, r.code, ...(r.country_names || [])].some((v) =>
        String(v || "").toLowerCase().includes(q)
      )
    );
  }, [rows, search]);

  // Back to the first page when the search changes; stay in range after a delete.
  useEffect(() => {
    setPage(1);
  }, [search]);
  const currentPage = Math.min(page, Math.max(1, Math.ceil(filtered.length / pageSize)));
  const pageRows = filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ is_active: true, sort_order: rows.length + 1 } as any);
    setModalOpen(true);
  };

  const openEdit = (row: RegionDto) => {
    setEditing(row);
    form.resetFields();
    form.setFieldsValue({
      name: row.name,
      code: row.code,
      is_active: row.is_active,
      sort_order: row.sort_order,
    });
    setModalOpen(true);
  };

  const onSave = async (values: FormValues) => {
    setSaving(true);
    const payload = {
      data: { ...values, code: String(values.code || "").trim().toLowerCase() },
    };
    try {
      if (editing) {
        await PUT(`${API.EPAPER_SETTINGS_REGIONS}/${editing.id}`, payload);
        message.success("Region updated");
      } else {
        await POST(API.EPAPER_SETTINGS_REGIONS, payload);
        message.success("Region added");
      }
      setModalOpen(false);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not save region."));
    } finally {
      setSaving(false);
    }
  };

  const toggleStatus = async (row: RegionDto, active: boolean) => {
    setTogglingId(row.id);
    try {
      await PATCH(`${API.EPAPER_SETTINGS_REGIONS}/${row.id}/status`, {
        data: { is_active: active },
      });
      message.success(`${row.name} ${active ? "enabled" : "disabled"}`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not update status."));
    } finally {
      setTogglingId(null);
    }
  };

  const remove = async (row: RegionDto) => {
    try {
      await DELETE(`${API.EPAPER_SETTINGS_REGIONS}/${row.id}`);
      message.success(`${row.name} deleted`);
      await afterChange();
    } catch (err) {
      message.error(apiErrorMessage(err, "Could not delete region."));
    }
  };

  const columns: ColumnsType<RegionDto> = [
    {
      title: "#",
      key: "index",
      width: 50,
      render: (_t, _r, i) => (currentPage - 1) * pageSize + i + 1,
    },
    { title: "Region Name", dataIndex: "name", key: "name" },
    { title: "Region Code", dataIndex: "code", key: "code", width: 120 },
    {
      title: "Countries",
      key: "countries",
      render: (_t, row) =>
        row.country_names?.length ? (
          <Tooltip title="Set a country's region from E-Paper Settings → Countries">
            {row.country_names.join(", ")}
          </Tooltip>
        ) : (
          "—"
        ),
    },
    { title: "Display Order", dataIndex: "sort_order", key: "sort_order", width: 120 },
    {
      title: "Status",
      key: "is_active",
      width: 140,
      render: (_t, row) => (
        <Space>
          <Switch
            size="small"
            checked={row.is_active}
            loading={togglingId === row.id}
            onChange={(checked) => toggleStatus(row, checked)}
          />
          <span>{row.is_active ? "Active" : "Inactive"}</span>
        </Space>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 100,
      render: (_t, row) => (
        <Space size="middle">
          <BiEdit size={20} color="#f5a442" cursor="pointer" onClick={() => openEdit(row)} />
          <Popconfirm
            title="Delete Region"
            description={`Delete ${row.name}? Regions in use can only be disabled.`}
            okText="Yes"
            cancelText="No"
            onConfirm={() => remove(row)}
          >
            <IoTrashOutline size={20} color="red" cursor="pointer" />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title={"Regions"}
        loader={loading}
        length={"Dashboard / E-Paper Settings / Regions"}
        onBack={() => navigate("/Auth/epaper-settings")}
      >
        <div className="homescreen-pageItem">
          <Input.Search
            style={{ width: 250 }}
            allowClear
            placeholder="Search region..."
            onChange={(e) => setSearch(e.target.value)}
          />
          &nbsp;
          <Button type="primary" onClick={openCreate}>
            + Add Region
          </Button>
        </div>
      </PageHeader>
      <div className="epaperSettings-subtitle" style={{ paddingTop: 10 }}>
        Manage regions. Assign each country to a region from Countries; the
        Add Agent form picks the selected country's region automatically.
      </div>
      <Table
        columns={columns}
        dataSource={pageRows}
        rowKey="id"
        size="small"
        loading={loading}
        pagination={false}
      />
      <br />
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Pagination
          responsive
          current={currentPage}
          total={filtered.length}
          pageSize={pageSize}
          onChange={(p, ps) => {
            setPage(p);
            setPageSize(ps);
          }}
        />
      </div>
      <br />

      <Modal
        title={editing ? "Edit Region" : "Add Region"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={560}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSave} requiredMark>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label="Region Name"
                name="name"
                rules={[{ required: true, whitespace: true, message: "Region name is required" }]}
              >
                <Input placeholder="Gulf" maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Region Code"
                name="code"
                extra={
                  editing
                    ? "Cannot be changed (saved on agents)"
                    : "3–20 letters/digits, unique, e.g. gulf"
                }
                rules={[
                  { required: true, message: "Region code is required" },
                  { pattern: /^[A-Za-z0-9]{3,20}$/, message: "3–20 letters or digits" },
                ]}
              >
                <Input
                  placeholder="gulf"
                  maxLength={20}
                  disabled={!!editing}
                  style={{ textTransform: "lowercase" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label="Display Order"
                name="sort_order"
                rules={[{ type: "integer", min: 0, message: "Whole number ≥ 0" }]}
              >
                <InputNumber min={0} precision={0} style={{ width: "100%" }} />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item label="Status" name="is_active" rules={[{ required: true }]}>
                <Radio.Group>
                  <Radio value={true}>Active</Radio>
                  <Radio value={false}>Inactive</Radio>
                </Radio.Group>
              </Form.Item>
            </Col>
          </Row>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Button onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={saving}>
              Save
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default EpaperRegions;
